/**
 * ?debug=streams — corner overlay showing every chat's streaming state.
 *
 * Renders directly into a fixed DOM node (no Preact tree) so it cannot be
 * affected by routing, Suspense boundaries, or auth gates. Visible only
 * when the URL contains `?debug=streams` (or the hash does).
 *
 * Lists per chat: id, streaming flag, lastSeq, content length (chars),
 * and age since stream started.
 */

import { effect } from '@preact/signals';
import { chatStreamStates, type ChatStreamState } from '../../modules/chat/chat-state';

function isEnabled(): boolean {
  // Query-string only. Do not honor URL fragments — a link shared in an
  // incoming chat message could trivially carry `#debug=streams` and the
  // fragment is set on navigation without requiring a full page load.
  return /[?&]debug=streams\b/.test(window.location.search);
}

function formatAge(startedAt: number | null): string {
  if (!startedAt) return '-';
  const ms = Date.now() - startedAt;
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function td(text: string): HTMLTableCellElement {
  const cell = document.createElement('td');
  cell.style.textAlign = 'left';
  cell.style.paddingRight = '8px';
  cell.textContent = text;
  return cell;
}

function th(text: string): HTMLTableCellElement {
  const cell = document.createElement('th');
  cell.style.textAlign = 'left';
  cell.style.paddingRight = '8px';
  cell.textContent = text;
  return cell;
}

function render(host: HTMLElement, states: Record<number, ChatStreamState>): void {
  const ids = Object.keys(states).sort((a, b) => Number(a) - Number(b));

  while (host.firstChild) host.removeChild(host.firstChild);

  const title = document.createElement('div');
  title.style.fontWeight = '600';
  title.style.marginBottom = '4px';
  title.textContent = `streams (${ids.length})`;
  host.appendChild(title);

  const table = document.createElement('table');
  table.style.fontFamily = 'ui-monospace,monospace';
  table.style.fontSize = '11px';
  table.style.borderCollapse = 'collapse';

  const thead = document.createElement('thead');
  const headRow = document.createElement('tr');
  headRow.style.opacity = '0.6';
  for (const label of ['chat', 'stream', 'seq', 'chars', 'age', 'queue']) {
    headRow.appendChild(th(label));
  }
  thead.appendChild(headRow);
  table.appendChild(thead);

  const tbody = document.createElement('tbody');
  if (ids.length === 0) {
    const row = document.createElement('tr');
    const cell = document.createElement('td');
    cell.colSpan = 6;
    cell.textContent = 'none';
    row.appendChild(cell);
    tbody.appendChild(row);
  } else {
    for (const id of ids) {
      const s = states[Number(id)];
      const row = document.createElement('tr');
      row.appendChild(td(id));
      row.appendChild(td(s.streaming ? 'YES' : 'no'));
      row.appendChild(td(String(s.lastSeq)));
      row.appendChild(td(String(s.content.length)));
      row.appendChild(td(formatAge(s.startedAt)));
      row.appendChild(td(String(s.queuedChars || 0)));
      tbody.appendChild(row);
    }
  }
  table.appendChild(tbody);
  host.appendChild(table);
}

/**
 * Mount the overlay if `?debug=streams` is set. Idempotent — safe to call
 * multiple times.
 */
export function startDebugStreamsOverlay(): void {
  if (!isEnabled()) return;
  if (document.getElementById('jarvis-debug-streams')) return;

  const host = document.createElement('div');
  host.id = 'jarvis-debug-streams';
  Object.assign(host.style, {
    position: 'fixed',
    bottom: '8px',
    right: '8px',
    zIndex: '99999',
    background: 'rgba(0,0,0,0.82)',
    color: '#fff',
    padding: '8px 10px',
    borderRadius: '6px',
    maxWidth: '360px',
    maxHeight: '40vh',
    overflow: 'auto',
    pointerEvents: 'auto',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
  });
  document.body.appendChild(host);

  effect(() => {
    render(host, chatStreamStates.value);
  });

  setInterval(() => render(host, chatStreamStates.value), 500);
}
